import {
    SchedulesReportResponse,
    SchedulesReportRow,
} from "@common/schemas/domains/accounting/schedules-report.schema";
import {
    SCHEDULES_ROLLFORWARD_COLUMNS,
    SchedulesReportLayout,
    SchedulesReportRowKind,
    SchedulesRollforwardColumn,
} from "@common/types/reports/schedules-report";
import { SchedulesReportViewRow } from "./schedulesReportViewTypes";

const TOTAL_ROW_KINDS = new Set<SchedulesReportRowKind>([
    SchedulesReportRowKind.templateTotal,
    SchedulesReportRowKind.holdingAccountSubtotal,
    SchedulesReportRowKind.counterpartyTotal,
    SchedulesReportRowKind.grandTotal,
]);

const ANALYSIS_ROW_KINDS = new Set<SchedulesReportRowKind>([
    SchedulesReportRowKind.analysisCurrent,
    SchedulesReportRowKind.analysisNonCurrent,
]);

export const SCHEDULES_WATERFALL_SUMMARY_FIELDS = [
    "booked",
    "recognized",
    "remaining",
] as const;
export type SchedulesWaterfallSummaryField =
    (typeof SCHEDULES_WATERFALL_SUMMARY_FIELDS)[number];

export function getWaterfallSummaryValueIndex({
    periodCount,
    field,
}: {
    periodCount: number;
    field: SchedulesWaterfallSummaryField;
}): number {
    return periodCount + SCHEDULES_WATERFALL_SUMMARY_FIELDS.indexOf(field);
}

export function getValueColumnsPerPeriod(
    layout: SchedulesReportLayout,
): number {
    return layout === SchedulesReportLayout.rollforward
        ? SCHEDULES_ROLLFORWARD_COLUMNS.length
        : 1;
}

function buildRollforwardValues(
    row: SchedulesReportRow,
    periodCount: number,
    isAnalysis: boolean,
): Array<number | null> {
    return Array.from({ length: periodCount }, (_, periodIndex) => {
        const cell = row.rollforward?.[periodIndex];
        return SCHEDULES_ROLLFORWARD_COLUMNS.map((column) => {
            if (!cell) {
                return null;
            }
            if (isAnalysis && column !== SchedulesRollforwardColumn.ending) {
                return null;
            }
            return cell[column];
        });
    }).flat();
}

function buildViewRow(
    row: SchedulesReportRow,
    report: Pick<SchedulesReportResponse, "periods" | "meta">,
): SchedulesReportViewRow {
    const isAnalysis = ANALYSIS_ROW_KINDS.has(row.rowKind);
    const isRollforward =
        report.meta.appliedLayout === SchedulesReportLayout.rollforward;
    const periodCount = report.periods.length;
    const children = row.children?.map((child) => buildViewRow(child, report));

    return {
        id: row.key,
        title: row.label,
        rowKind: row.rowKind,
        termStart: row.termStart,
        termEnd: row.termEnd,
        values: isRollforward
            ? buildRollforwardValues(row, periodCount, isAnalysis)
            : [
                  ...Array.from(
                      { length: periodCount },
                      (_, periodIndex) =>
                          row.waterfall?.[periodIndex]?.recognized ?? 0,
                  ),
                  ...SCHEDULES_WATERFALL_SUMMARY_FIELDS.map(
                      (field) => row.summary?.[field] ?? 0,
                  ),
              ],
        ...(!isRollforward && {
            isPostedByPeriod: Array.from(
                { length: periodCount },
                (_, periodIndex) =>
                    row.waterfall?.[periodIndex]?.isPosted ?? true,
            ),
        }),
        isTotal: TOTAL_ROW_KINDS.has(row.rowKind),
        isAnalysis,
        isExpandable: (children?.length ?? 0) > 0,
        drill: row.drill,
        scheduleApplicationId: row.scheduleApplicationId,
        templateId: row.templateId,
        counterpartyName: row.counterpartyName,
        ...(children && children.length > 0 && { children }),
    };
}

export function buildSchedulesReportRows(
    report: Pick<SchedulesReportResponse, "periods" | "meta" | "rows">,
): SchedulesReportViewRow[] {
    return report.rows.map((row) => buildViewRow(row, report));
}

/** Top-level expandable rows (templates, counterparties) start open; application rows stay closed so analysis children are hidden until expanded. */
export function getSchedulesReportDefaultExpandedState(
    rows: SchedulesReportViewRow[],
): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    for (const row of rows) {
        if (row.isExpandable) {
            state[row.id] = true;
        }
    }
    return state;
}
